import { useState } from "react";
import { Button } from "@/components/ui/button";

const YOUTUBE_ID_LENGTH = 11;

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getYoutubeEmbedUrl(url) {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  if (match && match[2].length === YOUTUBE_ID_LENGTH) {
    return `https://www.youtube.com/embed/${match[2]}`;
  }
  return null;
}

const ArticleMedia = ({ article, youtubeEmbedUrl, expanded, onExpand }) => {
  if (youtubeEmbedUrl) {
    return (
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        <iframe
          className="absolute top-0 left-0 w-full h-full"
          src={youtubeEmbedUrl}
          title={article.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  if (article.image_url) {
    return (
      <img
        src={expanded ? article.image_url : (article.thumbnail_url || article.image_url)}
        alt={article.title}
        className={`w-full object-cover ${!expanded ? "h-48 cursor-pointer" : ""}`}
        onClick={() => !expanded && onExpand()}
      />
    );
  }
  return null;
};

const ShareButtons = ({ articleId, articleTitle }) => {
  const [copied, setCopied] = useState(false);
  const articleUrl = `${window.location.origin}/article/${articleId}`;
  const shareText = `${articleTitle} - UK Discovered`;

  const shareToX = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(articleUrl)}`, "_blank", "width=550,height=420");
  };

  const shareToBluesky = () => {
    window.open(`https://bsky.app/intent/compose?text=${encodeURIComponent(shareText + " " + articleUrl)}`, "_blank", "width=550,height=420");
  };

  const shareToInstagram = async () => {
    try {
      await navigator.clipboard.writeText(articleUrl);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = articleUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    window.open("https://www.instagram.com/", "_blank");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(articleUrl);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = articleUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-800">
      <Button data-testid={`share-x-${articleId}`} onClick={shareToX} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
        <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
        Share
      </Button>
      <Button data-testid={`share-bluesky-${articleId}`} onClick={shareToBluesky} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
        <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor"><path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" /></svg>
        Bluesky
      </Button>
      <Button data-testid={`share-instagram-${articleId}`} onClick={shareToInstagram} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
        <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
        Instagram
      </Button>
      <Button data-testid={`copy-url-${articleId}`} onClick={copyUrl} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
        {copied ? (
          <><svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Copied!</>
        ) : (
          <><svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Copy URL</>
        )}
      </Button>
    </div>
  );
};

const ExpandToggle = ({ expanded, onToggle }) => (
  <button onClick={onToggle} className="mt-4 text-blue-400 hover:text-blue-300 font-semibold text-sm flex items-center gap-1 transition-colors">
    {expanded ? (
      <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>Show less</>
    ) : (
      <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>Read full article</>
    )}
  </button>
);

const ArticleCard = ({ article, onEdit, onDelete, isAdmin, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const youtubeEmbedUrl = getYoutubeEmbedUrl(article.youtube_url);

  return (
    <article data-testid={`article-${article.id}`} className="bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 hover:border-slate-700 transition-colors">
      <ArticleMedia article={article} youtubeEmbedUrl={youtubeEmbedUrl} expanded={expanded} onExpand={() => setExpanded(true)} />
      <div className="p-6">
        <h3 className="text-xl font-bold text-white mb-2">{article.title}</h3>
        <p className="text-slate-400 text-sm mb-4">By {article.author} &bull; {formatDate(article.created_at)}</p>
        <div className={`text-slate-300 whitespace-pre-wrap ${!expanded ? "line-clamp-4" : ""}`}>{article.content}</div>
        <ExpandToggle expanded={expanded} onToggle={() => setExpanded(!expanded)} />
        <ShareButtons articleId={article.id} articleTitle={article.title} />
        {isAdmin && (
          <div className="flex gap-3 mt-4 pt-4 border-t border-slate-800">
            <Button data-testid={`edit-article-${article.id}`} onClick={() => onEdit(article)} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 flex-1 sm:flex-initial px-6 py-3 min-h-[44px] text-base font-semibold">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              Edit
            </Button>
            <Button data-testid={`delete-article-${article.id}`} onClick={() => onDelete(article.id)} variant="outline" className="border-red-600 text-red-400 hover:bg-red-900/30 flex-1 sm:flex-initial px-6 py-3 min-h-[44px] text-base font-semibold">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              Delete
            </Button>
          </div>
        )}
      </div>
    </article>
  );
};

export default ArticleCard;
